<script lang="ts">
  // Logged-out marketing homepage (`/`). Renders fully signed-out; logged-in
  // visitors are redirected to /duels by the App.svelte guard.
  // Marketing landing v2 (2026-07-29, approved via design-chooser artifact:
  // "Sticker Riot × Fight Night", option 1 "exactly as asked").
  // Integration contract preserved from v1: app_cta_view on mount,
  // app_cta_click on a store-badge tap, POST /auth/waitlist {source:'landing_cta'},
  // app_waitlist_signup on success (the admin app-funnel reads these three events).
  // Store badges: swap hrefs to the real App Store / Play listings at launch
  // (and replace the CSS-drawn badges with the official badge assets).
  import { onMount } from 'svelte';
  import { api } from '$shared/api';
  import { trackEvent } from '$lib/analytics.svelte';

  let ctaEmail = $state('');
  let ctaMsg = $state('');
  let ctaLoading = $state(false);
  let waitlistOpen = $state(false);
  let emailInput: HTMLInputElement | undefined = $state();

  // Explicit landing-view signal for the web->app funnel: the router only
  // fires `page_view` on hashchange, never on the initial load, so a direct
  // visitor to `/` (the CTA's audience) would otherwise be uncounted.
  onMount(() => {
    trackEvent('app_cta_view', { context: 'landing' });
  });

  function badgeTap(store: 'ios' | 'android') {
    trackEvent('app_cta_click', { context: 'landing', metadata: { store } });
    waitlistOpen = true;
    queueMicrotask(() => emailInput?.focus());
  }

  async function handleWaitlist(e: Event) {
    e.preventDefault();
    const email = ctaEmail.trim();
    if (!email || ctaLoading) return;
    ctaLoading = true;
    ctaMsg = '';
    try {
      const data = await api.post<{ message: string }>('/auth/waitlist', {
        email,
        source: 'landing_cta',
      });
      ctaMsg = data.message || "You're on the list. First invite goes to you.";
      trackEvent('app_waitlist_signup', { context: 'landing' });
      ctaEmail = '';
    } catch {
      ctaMsg = 'Something went wrong. Try again.';
    } finally {
      ctaLoading = false;
    }
  }
</script>

<div class="landing">
  <!-- HERO: sticker headline, then the fight-night chunk directly beneath -->
  <section class="hero">
    <span class="halftone ht-pink" aria-hidden="true"></span>
    <span class="halftone ht-cyan" aria-hidden="true"></span>
    <span class="beam beam-pink" aria-hidden="true"></span>
    <span class="beam beam-cyan" aria-hidden="true"></span>

    <h1 class="stickrow">
      <span class="stick s1">YOUR</span>
      <span class="stick s2">FEED</span>
      <span class="stick s3">IS</span>
      <span class="stick s4">BORING.</span>
    </h1>

    <div class="fn-chunk">
      <span class="fn-eyebrow">TONIGHT AND EVERY NIGHT</span>
      <h2 class="hero-headline">SONGS FIGHT.<span class="l2">YOU DECIDE.</span></h2>
      <p class="fn-sub">
        Music battles judged by real ears — never an algorithm. Vote the winners, find your next obsession, climb the
        week ranks.
      </p>
      <div class="duelbox" aria-hidden="true">
        <div class="drow">
          <span>MIDNIGHT STATIC</span>
          <div class="dbar"></div>
          <span class="dv dv-pink">128</span>
        </div>
        <div class="vs">VS</div>
        <div class="drow two">
          <span>NEON ANTHEM</span>
          <div class="dbar"></div>
          <span class="dv dv-cyan">96</span>
        </div>
      </div>
    </div>

    <div class="ctas">
      <div class="badges">
        <button class="badge" onclick={() => badgeTap('ios')}>
          <span class="bglyph" aria-hidden="true">&#63743;</span>
          <span class="btxt"><span class="btiny">Download on the</span><span class="bbig">App Store</span></span>
        </button>
        <button class="badge" onclick={() => badgeTap('android')}>
          <span class="bglyph" aria-hidden="true">&#9654;</span>
          <span class="btxt"><span class="btiny">Get it on</span><span class="bbig">Google Play</span></span>
        </button>
      </div>

      {#if waitlistOpen}
        <form class="waitlist" onsubmit={handleWaitlist}>
          <p class="wl-line">Not live yet — drop your email and the first invite is yours.</p>
          <div class="wl-row">
            <input
              class="input"
              type="email"
              required
              placeholder="your@email.com"
              bind:value={ctaEmail}
              bind:this={emailInput}
            />
            <button class="btn btn-primary btn-pill" type="submit" disabled={ctaLoading}>
              {ctaLoading ? 'Saving…' : 'Get the invite'}
            </button>
          </div>
          {#if ctaMsg}<p class="wl-msg">{ctaMsg}</p>{/if}
        </form>
      {/if}

      <p class="creator-links">
        Make music? <a href="#/register">Sign Up</a> and throw your tracks in the pit. Already in?
        <a href="#/login">Log In</a>.
      </p>
    </div>
  </section>

  <!-- GAMES WALL -->
  <section class="section">
    <h2 class="stick-h2">THE GAMES</h2>
    <div class="wall">
      <div class="card">
        <span class="ic" aria-hidden="true">⚔️</span>
        <h3>Duels</h3>
        <p>Two songs enter. The crowd decides. Bragging rights forever.</p>
      </div>
      <div class="card">
        <span class="ic" aria-hidden="true">📈</span>
        <h3>Song Market</h3>
        <p>Called it before it was cool? Now there's a receipt.</p>
      </div>
      <div class="card">
        <span class="ic" aria-hidden="true">🌅</span>
        <h3>Daily Discovery</h3>
        <p>15 tracks, 3 minutes, underground gems!</p>
      </div>
      <div class="card">
        <span class="ic" aria-hidden="true">🤖</span>
        <h3>AI vs You</h3>
        <p>Score a track. See if your ears beat the machine.</p>
      </div>
    </div>

    <!-- CREATORS -->
    <div class="creator">
      <h2 class="stick-h2 gold">CREATORS THIS WAY</h2>
      <p>Web is your backstage: add tracks, enter duels, watch the crowd go at it.</p>
      <div class="pillrow">
        <a href="#/register" class="btn btn-primary btn-pill">Sign Up</a>
        <a href="#/login" class="btn btn-outline btn-pill">Log In</a>
      </div>
    </div>
  </section>

  <footer class="landing-footer">
    <a href="#/terms">Terms</a>
    <a href="#/privacy">Privacy</a>
  </footer>
</div>

<style>
  .landing {
    background: #0d0a10;
    overflow-x: hidden;
  }

  /* ---------- hero ---------- */
  .hero {
    position: relative;
    max-width: 980px;
    margin: 0 auto;
    padding: 56px 24px 20px;
    text-align: center;
  }
  .halftone {
    position: absolute;
    width: 150px;
    height: 150px;
    border-radius: 50%;
    background-size: 12px 12px;
    opacity: 0.14;
    pointer-events: none;
  }
  .ht-pink {
    top: 30px;
    left: 5%;
    background-image: radial-gradient(var(--pink) 1.8px, transparent 1.8px);
  }
  .ht-cyan {
    bottom: -20px;
    right: 7%;
    background-image: radial-gradient(var(--cyan) 1.8px, transparent 1.8px);
  }
  .beam {
    position: absolute;
    top: 0;
    width: 90px;
    height: 420px;
    pointer-events: none;
    animation: beam 7s ease-in-out infinite;
  }
  .beam-pink {
    left: 10%;
    background: linear-gradient(to bottom, rgba(255, 45, 120, 0.16), transparent 80%);
  }
  .beam-cyan {
    right: 12%;
    background: linear-gradient(to bottom, rgba(0, 229, 255, 0.13), transparent 80%);
    animation-duration: 9s;
  }

  /* Boxy full-stack hero (2026-07-31): one word per tile, stacked — matches the
     app welcome carousel's slide 1, which adopted this hero's colors in return. */
  .stickrow {
    display: flex;
    flex-direction: column;
    align-items: center;
    gap: 14px;
    background: none;
    -webkit-text-fill-color: initial;
  }
  .stick {
    display: inline-block;
    background: var(--surface-2);
    border: 3px solid #f5f5f0;
    border-radius: 14px;
    padding: 8px 26px;
    font-weight: 900;
    font-size: clamp(40px, 7.5vw, 84px);
    letter-spacing: -1px;
    line-height: 1.1;
    box-shadow: 6px 6px 0 rgba(0, 0, 0, 0.6);
    animation: wobble 3.2s ease-in-out infinite alternate;
    color: var(--text);
  }
  .stick.s1 {
    transform: rotate(-3deg);
  }
  .stick.s2 {
    transform: rotate(2deg);
    animation-delay: 0.3s;
  }
  .stick.s3 {
    transform: rotate(-2deg);
    background: var(--pink);
    color: #fff;
    animation-delay: 0.6s;
  }
  .stick.s4 {
    transform: rotate(1.5deg);
    background: var(--green);
    color: #0a0a0a;
    animation-delay: 0.9s;
  }

  /* ---------- fight-night chunk ---------- */
  .fn-chunk {
    margin-top: 36px;
    position: relative;
    z-index: 2;
  }
  .fn-eyebrow {
    display: inline-block;
    font-weight: 800;
    font-size: 12px;
    letter-spacing: 3px;
    color: #0a0a0a;
    background: var(--gold);
    padding: 5px 14px;
    transform: skewX(-8deg);
    border-radius: 4px;
  }
  .hero-headline {
    font-weight: 900;
    font-style: italic;
    text-transform: uppercase;
    font-size: clamp(40px, 7vw, 78px);
    line-height: 0.98;
    letter-spacing: -1px;
    margin-top: 20px;
    color: var(--text);
    background: none;
    -webkit-text-fill-color: initial;
  }
  .hero-headline .l2 {
    display: block;
    background: linear-gradient(100deg, var(--pink), var(--cyan));
    -webkit-background-clip: text;
    background-clip: text;
    color: transparent;
    -webkit-text-fill-color: transparent;
  }
  .fn-sub {
    font-size: 17px;
    line-height: 1.6;
    color: #b9b9b9;
    max-width: 520px;
    margin: 18px auto 0;
  }
  .duelbox {
    max-width: 560px;
    margin: 30px auto 0;
    background: rgba(20, 20, 20, 0.7);
    border: 1px solid #333;
    border-radius: 16px;
    padding: 18px;
  }
  .drow {
    display: flex;
    align-items: center;
    gap: 10px;
    font-family: ui-monospace, SFMono-Regular, Menlo, monospace;
    font-weight: 700;
    font-size: 12px;
  }
  .dbar {
    height: 14px;
    border-radius: 99px;
    background: linear-gradient(90deg, var(--pink), var(--purple));
    animation: race1 2.6s ease-in-out infinite alternate;
    box-shadow: 0 0 12px -3px var(--pink);
    flex: none;
  }
  .drow.two .dbar {
    background: linear-gradient(90deg, var(--cyan), var(--purple));
    animation: race2 2.6s ease-in-out infinite alternate;
    box-shadow: 0 0 12px -3px var(--cyan);
  }
  .dv-pink {
    color: var(--pink);
  }
  .dv-cyan {
    color: var(--cyan);
  }
  .vs {
    text-align: center;
    font-weight: 900;
    font-style: italic;
    font-size: 22px;
    color: var(--gold);
    margin: 10px 0;
    text-shadow: 0 0 16px rgba(255, 215, 0, 0.5);
    animation: pulsy 1.8s ease-in-out infinite alternate;
  }

  /* ---------- CTAs ---------- */
  .ctas {
    margin-top: 30px;
    display: flex;
    flex-direction: column;
    align-items: center;
    gap: 14px;
    position: relative;
    z-index: 3;
  }
  .badges {
    display: flex;
    gap: 12px;
    flex-wrap: wrap;
    justify-content: center;
  }
  .badge {
    display: flex;
    align-items: center;
    gap: 9px;
    background: #000;
    border: 2.5px solid #f5f5f0;
    border-radius: 12px;
    padding: 9px 16px;
    min-width: 150px;
    box-shadow: 4px 4px 0 rgba(0, 0, 0, 0.65);
    transform: rotate(-1deg);
    cursor: pointer;
    color: var(--text);
    font: inherit;
    text-align: left;
  }
  .badge + .badge {
    transform: rotate(1.2deg);
  }
  .badge:hover {
    border-color: var(--pink);
  }
  .bglyph {
    font-size: 21px;
    line-height: 1;
  }
  .btiny {
    display: block;
    font-size: 8.5px;
    color: #9a9a9a;
    letter-spacing: 0.4px;
  }
  .bbig {
    display: block;
    font-weight: 600;
    font-size: 14px;
    color: #fff;
    margin-top: 1px;
  }

  .waitlist {
    width: 100%;
    max-width: 460px;
  }
  .wl-line {
    font-size: 13px;
    font-weight: 600;
    color: #c9c9c9;
    margin-bottom: 10px;
  }
  .wl-row {
    display: flex;
    gap: 10px;
  }
  .wl-row .input {
    flex: 1;
  }
  .wl-msg {
    margin-top: 10px;
    font-size: 13px;
    font-weight: 600;
    color: var(--green);
  }

  .creator-links {
    font-size: 13px;
    font-weight: 600;
    color: var(--text-muted);
  }
  .creator-links a {
    color: var(--cyan);
    font-weight: 700;
  }
  .creator-links a:hover {
    color: var(--pink);
  }

  /* ---------- games wall + creators ---------- */
  .section {
    max-width: 1080px;
    margin: 0 auto;
    padding: 52px 24px 0;
    text-align: center;
  }
  .stick-h2 {
    display: inline-block;
    font-weight: 900;
    font-size: 18px;
    background: var(--cyan);
    color: #0a0a0a;
    padding: 5px 12px;
    border-radius: 6px;
    transform: rotate(-1.5deg);
    box-shadow: 3px 3px 0 rgba(0, 0, 0, 0.55);
    text-transform: none;
  }
  .stick-h2.gold {
    background: var(--gold);
  }
  .wall {
    display: grid;
    grid-template-columns: repeat(auto-fit, minmax(215px, 1fr));
    gap: 16px;
    margin-top: 22px;
    text-align: left;
  }
  .card {
    background: var(--surface);
    border: 2.5px solid #f5f5f0;
    border-radius: 14px;
    padding: 15px;
    box-shadow: 5px 5px 0 rgba(0, 0, 0, 0.55);
  }
  .card:nth-child(odd) {
    transform: rotate(-1deg);
  }
  .card:nth-child(even) {
    transform: rotate(1deg);
  }
  .card .ic {
    font-size: 22px;
  }
  .card h3 {
    font-weight: 800;
    font-size: 14px;
    margin-top: 6px;
    color: var(--text);
  }
  .card p {
    font-size: 12px;
    line-height: 1.5;
    font-weight: 500;
    color: var(--text-muted);
    margin-top: 4px;
  }

  .creator {
    margin-top: 54px;
    background: var(--surface-2);
    border: 2.5px dashed #666;
    border-radius: 16px;
    padding: 26px;
  }
  .creator p {
    font-size: 14px;
    font-weight: 600;
    color: #c9c9c9;
    margin: 12px 0 16px;
  }
  .pillrow {
    display: flex;
    gap: 12px;
    justify-content: center;
  }

  .landing-footer {
    max-width: 1080px;
    margin: 48px auto 0;
    padding: 20px 24px 40px;
    border-top: 1px solid var(--border);
    display: flex;
    gap: 18px;
    font-size: 12px;
    font-weight: 500;
    color: var(--text-muted);
    flex-wrap: wrap;
  }
  .landing-footer a {
    color: var(--text-muted);
  }
  .landing-footer a:hover {
    color: var(--cyan);
  }

  /* ---------- motion ---------- */
  @keyframes race1 {
    from {
      width: 34%;
    }
    to {
      width: 62%;
    }
  }
  @keyframes race2 {
    from {
      width: 55%;
    }
    to {
      width: 41%;
    }
  }
  @keyframes pulsy {
    from {
      transform: scale(0.96);
    }
    to {
      transform: scale(1.05);
    }
  }
  @keyframes beam {
    0%,
    100% {
      transform: translateX(-50px) skewX(-16deg);
    }
    50% {
      transform: translateX(50px) skewX(-16deg);
    }
  }
  @keyframes wobble {
    from {
      transform: rotate(-2.4deg);
    }
    to {
      transform: rotate(1.8deg);
    }
  }
  @media (prefers-reduced-motion: reduce) {
    .stick,
    .dbar,
    .vs,
    .beam {
      animation: none !important;
    }
  }
</style>
